import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import { BaSeDialogContext } from '@base/react';
import { v2, version } from '../../constants/endpoints';

const contaEndpoint = `${version}/conta`;

export const useGerenciarContasService = () => {
  const [loadingConta, setLoadingConta] = React.useState(false);
  const [listaBancos, setListaBancos] = React.useState(false);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { backendPost, backendPut, backendDelete, backendGet } =
    Sebrae.useBackend();

  const deletarConta = React.useCallback(
    async (contaId) => {
      setLoadingConta(true);
      await backendDelete(`${contaEndpoint}/${contaId}`)
        .then(() =>
          showNotification({
            color: 'confirmation',
            message: 'Conta excluída com sucesso.',
          }),
        )
        .catch((error) =>
          showNotification({
            color: 'destructive',
            message: 'Erro ao excluir conta.',
            supportMessage: error?.message ?? '',
          }),
        )
        .finally(() => setLoadingConta(false));
    },
    [backendDelete, showNotification],
  );

  const showMessageError = React.useCallback(
    (error) =>
      showNotification({
        color: 'destructive',
        message: 'Erro ao salvar conta.',
        supportMessage: error?.message,
      }),
    [showNotification],
  );

  const salvarConta = React.useCallback(
    async (empresaId, payload, backToGrid, isLoading) => {
      await backendPost(`${contaEndpoint}/${empresaId}`, payload)
        .then(() => {
          showNotification({
            message: 'Conta salvo com sucesso!',
            color: 'confirmation',
          });
          backToGrid();
        })
        .catch((error) => showMessageError(error))
        .finally(() => isLoading(false));
    },
    [backendPost, showMessageError, showNotification],
  );

  const editarConta = React.useCallback(
    async (contaId, payload, backToGrid, isLoading) => {
      await backendPut(`${contaEndpoint}/${contaId}`, payload)
        .then(() => {
          showNotification({
            message: 'Conta editado com sucesso!',
            color: 'confirmation',
          });
          backToGrid();
        })
        .catch((error) => showMessageError(error))
        .finally(() => isLoading(false));
    },
    [backendPut, showMessageError, showNotification],
  );

  const getBancos = React.useCallback(async () => {
    await backendGet(`${version}/banco`)
      .then(setListaBancos)
      .catch((error) =>
        showNotification({
          color: 'destructive',
          message: 'Erro ao obter lista de bancos.',
          supportMessage: error?.message,
        }),
      )
      .finally();
  }, [backendGet, showNotification]);

  return {
    deletarConta,
    salvarConta,
    editarConta,
    getBancos,
    listaBancos,
    loadingConta,
  };
};

export function useGetListaContas(empresaId, isEmptyValue = false) {
  const [listaContas, setListaContas] = React.useState([]);
  const [loadingContas, setLoadingContas] = React.useState();
  const [errorContas, setErrorContas] = React.useState(false);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    setLoadingContas(true);
    backendGet(`${contaEndpoint}/${empresaId}`)
      .then((item) => {
        setListaContas(() => {
          const contas = item.map((e) => ({
            id: e.id,
            label: e.nomeConta,
          }));

          return isEmptyValue ? [{ id: 0, label: '-' }, ...contas] : contas;
        });
      })
      .catch((error) => {
        setErrorContas(true);
        showNotification({
          message: 'Erro ao buscar lista de contas',
          supportMessage: error?.message ?? '',
          color: 'destructive',
        });
      })
      .finally(() => setLoadingContas(false));
  }, [backendGet, empresaId, isEmptyValue, showNotification]);

  return { listaContas, loadingContas, errorContas };
}

export function useGetListaContatos(empresaId, tipoContato) {
  const [listaContatos, setListaContatos] = React.useState([]);
  const [loadingContatos, setLoadingContatos] = React.useState();
  const [errorContatos, setErrorContatos] = React.useState(false);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    setLoadingContatos(true);
    backendGet(
      `${version}/contato/${empresaId}${
        tipoContato ? `?tipoContato=${tipoContato}` : ''
      }`,
    )
      .then((item) => {
        setListaContatos(() => {
          const contatos = item.map((e) => ({
            id: e.id,
            label: e.nome,
          }));

          return tipoContato ? [{ id: 0, label: '-' }, ...contatos] : contatos;
        });
      })
      .catch((error) => {
        setErrorContatos(true);
        showNotification({
          message: 'Erro ao buscar lista de contatos',
          supportMessage: error?.message ?? '',
          color: 'destructive',
        });
      })
      .finally(() => setLoadingContatos(false));
  }, [backendGet, empresaId, showNotification, tipoContato]);

  return { listaContatos, loadingContatos, errorContatos };
}

export function useGetListaFiltrosPredefinidos(empresaId) {
  const [listaFiltrosPredefinidos, setListaFiltrosPredefinidos] =
    React.useState([]);
  const [loadingListaFiltrosPredefinidos, setLoadingListaFiltrosPredefinidos] =
    React.useState();
  const [errorListaFiltrosPredefinidos, setErrorListaFiltrosPredefinidos] =
    React.useState(false);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { backendGet } = Sebrae.useBackend();

  React.useEffect(() => {
    setLoadingListaFiltrosPredefinidos(true);
    backendGet(`${v2}/relatorio/${empresaId}/filtros-predefinidos`)
      .then((item) => {
        setListaFiltrosPredefinidos(() => {
          return item.map((e) => ({
            ...e,
            label: e?.nome ?? '',
          }));
        });
      })
      .catch((error) => {
        setErrorListaFiltrosPredefinidos(true);
        showNotification({
          message: 'Erro ao buscar lista de filtros predefinidos.',
          supportMessage: error?.message ?? '',
          color: 'destructive',
        });
      })
      .finally(() => setLoadingListaFiltrosPredefinidos(false));
  }, [backendGet, empresaId, showNotification]);

  return {
    listaFiltrosPredefinidos,
    loadingListaFiltrosPredefinidos,
    errorListaFiltrosPredefinidos,
  };
}
